import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Truck, Target, Users, ArrowRight, MapPin } from 'lucide-react';

const AboutPage = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#111111]">
              About Transa
            </h1>
            <p className="mt-6 text-xl text-[#475569] leading-relaxed">
              Site logistics built for installers. We exist because modern installs create 
              packaging challenges that someone needed to solve properly.
            </p>
          </div>
        </div>
      </section>

      {/* Why Transa Exists */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111111] mb-6">Why Transa exists</h2>
              <div className="space-y-4 text-[#475569] text-lg leading-relaxed">
                <p>
                  Battery installations changed everything. Where a simple electrical job might 
                  have produced a few small boxes, a modern battery install generates mountains 
                  of packaging — inverter boxes, BMS units, multiple battery module boxes.
                </p>
                <p>
                  Installers are expected to remove all this packaging. But vans have limited space. 
                  Most installers operate from home with no commercial bins. The result? Cramped 
                  vehicles, weekend tip runs, and hours of lost productivity.
                </p>
                <p>
                  We built Transa to solve this problem properly. Professional reverse logistics 
                  designed specifically for the installer workflow.
                </p>
              </div>
            </div>
            <div className="bg-[#F8FAFC] rounded-2xl p-8 lg:p-12">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-[#0D9488] flex items-center justify-center mx-auto mb-6">
                  <Truck className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#111111]">
                  Site Logistics for Installers
                </h3>
                <p className="mt-4 text-[#475569]">
                  We collect bulky packaging from battery and electrical installs so you can keep working.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Phase 1 */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="card-hover border-[#E2E8F0]">
              <CardContent className="pt-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-[#0D9488]" />
                </div>
                <h3 className="text-xl font-bold text-[#111111]">Phase 1: Melbourne Metro</h3>
                <p className="mt-3 text-[#475569]">
                  We're starting with Metropolitan Melbourne LGAs to ensure reliable, 
                  consistent service before expanding.
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-[#E2E8F0]">
              <CardContent className="pt-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-[#0D9488]" />
                </div>
                <h3 className="text-xl font-bold text-[#111111]">Installer-First Focus</h3>
                <p className="mt-3 text-[#475569]">
                  Everything we do is designed around installer workflows. Simple booking, 
                  flexible scheduling, professional service.
                </p>
              </CardContent>
            </Card>
            <Card className="card-hover border-[#E2E8F0]">
              <CardContent className="pt-8 text-center">
                <div className="w-14 h-14 rounded-xl bg-[#F0FDFA] flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-[#0D9488]" />
                </div>
                <h3 className="text-xl font-bold text-[#111111]">Long-Term Vision</h3>
                <p className="mt-3 text-[#475569]">
                  Building towards a multi-industry logistics platform, starting with 
                  the installers who need us most.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#111111] mb-6">Our commitment</h2>
            <div className="grid sm:grid-cols-2 gap-6 text-left mt-8">
              {[
                { title: 'Professional operations', desc: 'Reliable scheduling, clear communication, quality service' },
                { title: 'Installer-friendly', desc: 'Designed around your workflow, not the other way around' },
                { title: 'Transparent pricing', desc: 'Clear models, no hidden fees, predictable costs' },
                { title: 'Responsible disposal', desc: 'We aim to sort and recycle through appropriate facilities' },
              ].map((item, index) => (
                <div key={index} className="bg-[#F8FAFC] rounded-xl p-6">
                  <h3 className="font-semibold text-[#111111]">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#64748B]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#111111]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Work with Transa</h2>
          <p className="text-[#94A3B8] mb-8 max-w-xl mx-auto">
            Ready to simplify your installer workflow? Let's talk about how we can help.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/pricing">
              <Button size="lg" className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-8">
                Request Pricing
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-[#374151] text-white hover:bg-[#1F2937] px-8">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
